/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, InputAdornment, IconButton, List, ListItem, ListItemIcon, ListItemText, Box, Backdrop, CircularProgress, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useEffect, useRef, useState, } from "react";

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Cancel, Close, Delete, Person, Save, Search, Warehouse } from '@mui/icons-material';
import { onlyNumbers } from '../../../common/CustomFunctions';
import DialogActorSearch from './DialogActorSearch';
import { IActorGuiModel } from '../../../model/IActorGuiModel';
import DialogUserSearch from './DialogUserSearch';
import { IApproverFlowGuiModel, IApproverFlowPostGuiModel, IApproverFlowPutGuiModel, } from '../../../model/IApproverGuiModel';
import { IUserContextGuiModel } from '../../../model/IUserContextGuiModel';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';

interface IDialogProps {
    organizationId: string | undefined,
    flow: IApproverFlowGuiModel,
    type: "put" | "post",
    open: boolean,
    closeDialog: (reason: "backdropClick" | "escapeKeyDown") => void,
}

const DialogEditFlow = (props : IDialogProps): JSX.Element => {

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    //##########################################################################################################
    const [name, setName] = useState(props.flow.name ?? "");
    const [amountLimit, setAmountLimit] = useState(props.flow.amountLimit ? props.flow.amountLimit : "");
    //##
    const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
    const handleOpenSupplierDialog = () => {
        setOpenSupplierDialog(true);
    }
    const handleCloseSupplierDialog = () => {
        setOpenSupplierDialog(false );
    }
    const [chosenSuppliers, setChosenSuppliers] = useState<IActorGuiModel[]>(
        props.flow.suppliers ? props.flow.suppliers : []
    );
    //##
    const [openUserDialog, setOpenUserDialog] = useState(false);
    const handleOpenUserDialog = () => {
        setOpenUserDialog(true);
    }
    const handleCLoseUserDialog = () => {
        setOpenUserDialog(false);
    }
    const [chosenUsers, setChosenUsers] = useState<IUserContextGuiModel[]>(
        props.flow.users ? props.flow.users : []
    );

    useEffect(() => {
        setName(props.flow.name);
        setAmountLimit(props.flow.amountLimit ? props.flow.amountLimit : "");
        setChosenSuppliers(props.flow.suppliers ? props.flow.suppliers : []);
        setChosenUsers(props.flow.users ? props.flow.users : [])
    },[props.flow])

    const validator = (): boolean => {
        if (!name || name === "") {
            toast(t("approver.nameEmpty"))
            return false;
        } else if (chosenUsers.length === 0) {
            toast(t("approver.noUsers"))
            return false;
        } else {
            return true;
        }
    }

    //#######################API calls########################################################

    const [loader, setLoader] = useState(false);

    const putFlow = async () => {

        if (validator()) {
            const data: IApproverFlowPutGuiModel = {
                name: { value: name },
                autoAssignable: props.flow.autoAssignable ? { value: props.flow.autoAssignable } : {value: true},
                amountLimit: amountLimit ? { value: amountLimit !== "" ? amountLimit : undefined } : {value: undefined},
                users: { value: chosenUsers.map(e => { return e.id }) },
                order: { value: props.flow.order },
                suppliers: chosenSuppliers ? { value: chosenSuppliers.map(e => { return e.supplierId as number }) } : undefined,
            }

            const logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.putApproverFlow(props.organizationId ? props.organizationId : "", props.flow.id, data);
            setLoader(false);

            if (result.hasErrors) {
                toast(t("approver.errorPutFlow") + result.errors.join(", "));
            } else {
                props.closeDialog("escapeKeyDown");
            }  
        }
    }
    const postFlow = async () => {

        if (validator()) {
            const data: IApproverFlowPostGuiModel = {
                name: name,
                autoAssignable: true,
                amountLimit: amountLimit ? amountLimit : undefined,
                users: chosenUsers.map(e => { return e.id }),
                suppliers: chosenSuppliers ? { value: chosenSuppliers.map(e => { return e.supplierId as number }) } : undefined,
            }

            const logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.postApproverFlow(props.organizationId ? props.organizationId : "", props.flow.id, data);
            setLoader(false);

            if (result.hasErrors) {
                toast(t("approver.errorPutFlow") + result.errors.join(", "));
            } else {
                props.closeDialog("escapeKeyDown");
            }
        }
        
    }
    const deleteFlow = async () => {

        const logic = new ApproverBusinessLogic();
        setLoader(true);
        const result = await logic.deleteApproverFlow(props.organizationId ? props.organizationId : "", props.flow.id);
        setLoader(false);

        if (result.hasErrors) {
            toast(t("approver.errorDeleteFlow") + result.errors.join(", "));
        } else {
            props.closeDialog("escapeKeyDown");
        }
    }


    //########################################################################################
    //########################################################################################
    return <Dialog maxWidth="sm" fullWidth={true} onClose={(event,reason) => props.closeDialog(reason)} open={props.open}>
        <DialogTitle display="flex" justifyContent="center">
            <Typography variant="button" fontSize="1rem">{t("approver.editApprovalFlow")}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: theme.spacing(1) }}>
            <Grid container direction="column" spacing={2} padding={theme.spacing(2)}>
                <Grid>
                    <TextField
                        size="small"
                        variant="outlined"
                        color="warning"
                        value={name}
                        fullWidth
                    error={name === "" ? true : false}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end" component={IconButton} onClick={() => setName("")}
                                    sx={{ display: name ? "inherit" : "none" }}><Close /></InputAdornment>,
                        }}
                        onChange={(e) => setName(e.target.value)}
                        id="nameId" label={t("basic.name")} />
                </Grid>
                <Grid>
                    <Stack component={Paper} elevation={3} padding={theme.spacing(2)} overflow="hidden" position="relative">
                        <Typography variant="button" alignSelf="center">{t("basic.conditions")}</Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            color="warning"
                            value={amountLimit}
                            fullWidth
                            sx={{ marginBottom: theme.spacing(2) }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position="end" component={IconButton} onClick={() => setAmountLimit("")}
                                        sx={{ display: amountLimit ? "inherit" : "none" }}><Close /></InputAdornment>,
                            }}
                            onChange={(e) => onlyNumbers(e.target.value, setAmountLimit)}
                            id="amountLimitId" label={t("basic.amountLimit")} />
                        <Button onClick={handleOpenSupplierDialog} fullWidth variant="contained" color="info" endIcon={<Search />}>
                            {t("basic.addSupplier")}
                        </Button>
                        <Box sx={{
                            height: "12rem",
                            overflowY: "scroll",
                            border: 1,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderBottomLeftRadius: theme.shape.borderRadius / 2,
                            borderBottomRightRadius: theme.shape.borderRadius / 2,
                            bgcolor: theme.palette.grey[100],
                            borderColor: theme.palette.grey[400],
                        }}>
                            <List dense disablePadding sx={{ padding: theme.spacing(1) }}>
                                {chosenSuppliers.map((e, index) => {
                                    return <ListItem disablePadding
                                        sx={{
                                            border: 1,
                                            borderRadius: theme.shape.borderRadius,
                                            borderColor: theme.palette.secondary.main,
                                            marginBottom: theme.spacing(1)
                                        }}
                                        secondaryAction={
                                            <IconButton onClick={() => setChosenSuppliers(chosenSuppliers.filter((e, innerIndex) => innerIndex !== index))}>
                                                <Delete color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon><Warehouse /></ListItemIcon>
                                        <ListItemText primary={e.name} />
                                    </ListItem>
                                })}
                            </List>
                        </Box>
                    </Stack>
                </Grid>
                <Grid>
                    <Stack component={Paper} elevation={3} padding={theme.spacing(2)} overflow="hidden" position="relative">
                        <Typography variant="button" alignSelf="center">{t("approver.flow")}</Typography>
                        <Button onClick={handleOpenUserDialog} fullWidth variant="contained" color="secondary" endIcon={<Search />}>
                            {t("basic.addUser")}
                        </Button>
                        <Box sx={{
                            height: "12rem",
                            overflowY: "scroll",
                            border: 1,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderBottomLeftRadius: theme.shape.borderRadius / 2,
                            borderBottomRightRadius: theme.shape.borderRadius / 2,
                            bgcolor: theme.palette.grey[100],
                            borderColor: theme.palette.grey[400],
                        }}>
                            <List dense disablePadding sx={{ padding: theme.spacing(1) }}>
                                {chosenUsers.map((e, index) => {
                                    return <ListItem disablePadding
                                        sx={{
                                            border: 1,
                                            borderRadius: theme.shape.borderRadius,
                                            borderColor: theme.palette.secondary.main,
                                            marginBottom: theme.spacing(1)
                                        }}
                                        secondaryAction={
                                            <IconButton onClick={() => setChosenUsers(chosenUsers.filter((e, innerIndex) => innerIndex !== index))}>
                                                <Delete color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon><Person /></ListItemIcon>
                                        <ListItemText primary={e.fullName} />
                                    </ListItem>
                                })}
                            </List>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogContent>
            <Grid container spacing={2}>
                <Grid xs={12} sm={5}>
                    <Button onClick={() => {
                        props.type === "put" ? putFlow() : postFlow();
                    }} fullWidth variant="contained" color="primary" endIcon={<Save />}>
                        {t("basic.save")}
                    </Button>
                </Grid>
                <Grid xs={7} sm={4}>
                    <Button onClick={() => props.closeDialog("escapeKeyDown")} fullWidth variant="contained" color="warning" endIcon={<Cancel />}>
                        {t("basic.cancel")}
                    </Button>
                </Grid>
                <Grid xs={5} sm={3}>
                    <Button
                        onClick={deleteFlow}
                        sx={{ display: props.type === "post" ? "none" : "inline-flex" }}
                        fullWidth
                        variant="contained"
                        color="error"
                        endIcon={<Delete />}>
                        {t("basic.delete")}
                    </Button>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActorSearch
            type={'Supplier'}
            organizationId={props.organizationId}
            dialogOpen={openSupplierDialog}
            handleDialogClose={handleCloseSupplierDialog}
            existingActors={chosenSuppliers}
            setActors={setChosenSuppliers} />
        <DialogUserSearch
            organizationId={props.organizationId}
            dialogOpen={openUserDialog}
            handleDialogClose={handleCLoseUserDialog}
            existingUsers={chosenUsers}
            setUsers={setChosenUsers} />
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </Dialog>

}
export default DialogEditFlow;